<template>
  <div>
    <b-sidebar
      id="sidebar-edit-contract"
      sidebar-class="sidebar-lg"
      :visible="changeConsultantRoleSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateChangeConsultantRoleSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Solicitar mudança de nível do consultor</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <div class="mb-2">
            <h5>{{ changeConsultantRoleSidebar.name }}</h5>
            <h6>{{ changeConsultantRoleSidebar.role }}</h6>
          </div>

          <b-form-group label="Novo nível" label-for="consultant-new-role">
            <v-select
              id="consultant-new-role"
              v-model="newConsultantRole"
              required
              :reduce="(role) => role.id_consultant_role"
              :options="availableRoles"
              label="name"
              :loading="loadingRoles"
              :class="getSelectErrorClass(v$.newConsultantRole.$error)"
              @input="getChangeIsValid"
            >
             <template #no-options="{ search, searching, loading }">
                Não há níveis disponíveis para este consultor
              </template>
            </v-select>
            <div class="invalid-feedback">
              <span v-if="v$.newConsultantRole.required.$invalid">
                Você deve informar um novo nível para o consultor
              </span>
            </div>
          </b-form-group>

          <b-form-group
            v-if="franchiseNameRequired"
            label="Nome da franquia"
            label-for="consultant-new-franchise"
          >
            <b-form-input
              id="consultant-new-franchise"
              type="text"
              v-model="newFranchiseName"
              :class="{
                'is-invalid': v$.newFranchiseName.$error,
              }"
            />
            <div class="invalid-feedback">
              <span v-if="v$.newFranchiseName.required.$invalid">
                Você deve informar um nome para a franquia do consultor
              </span>
            </div>
          </b-form-group>
          <div class="mb-2" v-if="!roleChangeIsValid">
            <span class="text-danger">
              A transferência para o nível selecionado não está disponível
              enquanto houverem consultores ativos abaixo do consultor
              escolhido.
            </span>
          </div>
          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              :disabled="saving || !roleChangeIsValid"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              <span>
                {{ saving ? "Salvando..." : "Salvar" }}
              </span>
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import _ from "lodash";
import {
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BFormInput,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { EXECUTIVE_ROLES, EXPANSAO_ROLE_ID } from "@/constants/consultants";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import { required, requiredIf } from "@vuelidate/validators";
import { getVueSelectErrorClass } from "@/helpers/validators";

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      saving: false,
      loadingRoles: false,
      roleChangeIsValid: true,
      newConsultantRole: undefined,
      newFranchiseName: undefined,
      promotionChecklistRequired: false,
      skipPromotion: false,
    };
  },
  computed: {
    ...mapGetters({
      changeConsultantRoleSidebar: types.CHANGE_CONSULTANT_ROLE_SIDEBAR,
      roles: sharedTypes.ROLES,
      promotionContractRules: types.PROMOTION_CONTRACT_RULES,
    }),
    franchiseNameRequired: function () {
      return this.newConsultantRole == EXPANSAO_ROLE_ID;
    },
    availableRoles: function() {
      if (!this.changeConsultantRoleSidebar.contractType || !this.promotionContractRules.length) {
        return this.roles;
      }
      const currentRoleRule = _.find(this.promotionContractRules, rule => {
        return rule.contract_type_id === this.changeConsultantRoleSidebar.contractType
          && rule.consultant_role_id === this.changeConsultantRoleSidebar.roleId;
      });
      if (!currentRoleRule) {
        return _.filter(this.roles, role => {
          return _.some(this.promotionContractRules, rule => {
            return rule.contract_type_id === this.changeConsultantRoleSidebar.contractType
              && rule.consultant_role_id === role.id_consultant_role
          });
        });
      }
      return _.filter(this.roles, role => {
        return _.some(this.promotionContractRules, rule => {
          return rule.contract_type_id === this.changeConsultantRoleSidebar.contractType
            && rule.consultant_role_id === role.id_consultant_role
            && rule.depth > currentRoleRule.depth;
        });
      });
    },
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      newConsultantRole: { required },
      newFranchiseName: {
        required: requiredIf((value) => {
          return this.franchiseNameRequired;
        }),
      },
    };
  },
  mounted() {
    this.loadingRoles = true;
    Promise.all([
      this.getRoles(),
      this.getPromotionContractRules()
    ])
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os dados para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loadingRoles = false;
      });
  },
  methods: {
    ...mapMutations({
      mutateChangeConsultantRoleSidebar:
        types.MUTATE_CHANGE_CONSULTANT_ROLE_SIDEBAR,
    }),
    ...mapActions({
      changeConsultantRole: types.CHANGE_CONSULTANT_ROLE,
      getRoles: sharedTypes.GET_ROLES,
      getRoleChangeIsValid: types.GET_ROLE_CHANGE_IS_VALID,
      getHasPendingPromotion: types.GET_HAS_PENDING_PROMOTION,
      getPromotionContractRules: types.GET_PROMOTION_CONTRACT_RULES,
    }),
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect || !this.roleChangeIsValid) return;
      this.saving = true;

      await this.changeConsultantRole({
        consultant_id: this.changeConsultantRoleSidebar.id,
        new_role: this.newConsultantRole,
        franchise_name: this.newFranchiseName,
        skip_promotion: this.skipPromotion
      })
        .then((response) => {
          const certificatePendency = response.data && response.data.certificate_pendency;
          if (certificatePendency) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Solicitação registrada com pendência",
                text: certificatePendency.message,
                icon: "AlertTriangleIcon",
                variant: "warning",
              },
            });
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Sucesso",
                text: "Solicitação de mudança de nível registrada com sucesso!",
                icon: "CoffeeIcon",
                variant: "success",
              },
            });
          }
          this.mutateChangeConsultantRoleSidebar({ visible: false });
          this.changeConsultantRoleSidebar.saveAction();
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: error.response.data.error,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao atualizar o nível do consultor. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    getChangeIsValid() {
      if (_.includes(EXECUTIVE_ROLES, this.newConsultantRole)) {
        this.getRoleChangeIsValid({
          consultant_id: this.changeConsultantRoleSidebar.id,
          new_role: this.newConsultantRole,
        })
          .then((response) => {
            this.roleChangeIsValid = response.data.valid;
          })
          .catch((error) => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao consultar se a mudança de nível é válida. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          });
      }
    },
    clear() {
      this.newConsultantRole = undefined;
      this.newFranchiseName = undefined;
      this.roleChangeIsValid = true;
      this.v$.$reset();
    },
  },
};
</script>
